import { templateRiskIncidentSidePanel } from '../view/template';
import { RiskIncidentSidePanelWebComponent } from '../index';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { RiskIncidentSidePanelView } from '../view/RiskIncidentSidePanelView';
import {
  LastSearchValues,
  EntityData,
  RisksData,
  TYPES_OF_RISKS,
  RiskIncidentSidePanelEvents,
  MisconfigurationData,
  DeviceData,
  VulnerabilityData,
} from '../types';
import { LabelSwitchEvents } from '../../../ui/gzLabelSwitch/types';
import { debounce } from '../../../utils/event.utils';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { NavigationConstants, NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as DevicesColumnKeys } from '../../../riskManagement/devicesApp/types';
import { ColumnKeys as VulnerabilitiesColumnKeys } from '../../../riskManagement/vulnerabilitiesApp/types';
import { ColumnKeys as MisconfigurationsColumnKeys } from '../../../riskManagement/misconfigurationsApp/types';

export class RiskIncidentSidePanelController extends BaseWebComponentController {
  public component: RiskIncidentSidePanelWebComponent;
  public view: RiskIncidentSidePanelView;
  public viewConstructor: typeof RiskIncidentSidePanelView;
  private data: RisksData;
  private searchKey: string = '';
  private lastSearchValues: LastSearchValues = {
    [TYPES_OF_RISKS.TYPE_ERA]: '',
    [TYPES_OF_RISKS.TYPE_CLOUD_SECURITY]: '',
  };

  constructor(component: RiskIncidentSidePanelWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: RiskIncidentSidePanelView,
      viewTemplate: templateRiskIncidentSidePanel,
    });
  }

  public async render(): Promise<void> {
    const result = await this.getDataForView();
    this.data = result;
    if (Object.keys(result[TYPES_OF_RISKS.TYPE_ERA]).includes(this.component.typeOfSectionFocused)) {
      this.component.type = TYPES_OF_RISKS.TYPE_ERA;
    }
    if (Object.keys(result[TYPES_OF_RISKS.TYPE_CLOUD_SECURITY]).includes(this.component.typeOfSectionFocused)) {
      this.component.type = TYPES_OF_RISKS.TYPE_CLOUD_SECURITY;
    }
    const processedResult = {
      data: result,
      risksType: this.component.type,
      sectionFocused: this.component.typeOfSectionFocused,
    };
    this.view.render(processedResult);
  }

  async getDataForView(): Promise<any> {
    const result = {};
    this.view.setLoadingState();
    result[TYPES_OF_RISKS.TYPE_ERA] = await this.dependencies.getXdrRisks(
      this.component.incidentId,
      TYPES_OF_RISKS.TYPE_ERA
    );
    result[TYPES_OF_RISKS.TYPE_CLOUD_SECURITY] = await this.dependencies.getXdrRisks(
      this.component.incidentId,
      TYPES_OF_RISKS.TYPE_CLOUD_SECURITY
    );
    this.view.removeLoadingState();
    return result;
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$titleHeader,
        event: LabelSwitchEvents.CHANGE,
        callback: this.handleLabelSwitchChange,
      },
      {
        element: this.view.dom.$inputSearch,
        event: 'change',
        callback: debounce(this.handleSearchInputChange, 700),
      },
      {
        element: this.view.dom.$inputSearch,
        event: 'change',
        callback: this.disableLabelSwitch,
      },
      {
        element: this.view.dom.$accordions,
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.handleMenuSelection,
      },
      {
        element: this.view.dom.$licenseRequiredContainer,
        event: ButtonEvents.CLICK,
        callback: this.clickOnGetLicense,
      },
    ];
  }

  private handleMenuSelection(ev: CustomEvent): void {
    const details = JSON.parse(ev.detail.selected);
    switch (details.event) {
      case RiskIncidentSidePanelEvents.OPEN_CLOUD_SECURITY_LINK:
        this.openLink(details.data);
        break;
      case RiskIncidentSidePanelEvents.PIVOT_TO_DEVICES:
      case RiskIncidentSidePanelEvents.PIVOT_TO_DEVICES_CLOUD:
        this.pivotToDevices(details.data);
        break;
      case RiskIncidentSidePanelEvents.PIVOT_TO_VULNERABILITIES:
        this.pivotToVulnerabilities(details.data);
        break;
      case RiskIncidentSidePanelEvents.PIVOT_TO_MISCONFIGURATION:
        this.pivotToMisconfiguration(details.data);
        break;
    }
  }

  private pivotToMisconfiguration(data: MisconfigurationData): void {
    const params = {
      filters: [
        {
          [MisconfigurationsColumnKeys.MISCONFIGURATION_NAME]: {
            complexEnumFilter: [{
              label: data.misconfigurationName,
              value: data.misconfigurationId
            }],
          },
        },
        {
          [MisconfigurationsColumnKeys.COMPANY]: {
            exactMatch: this.component.companyId
          },
        },
      ],
    };
    Application.lib.Navigation.openAppPageInNewTab('/risk-management-findings', params);
  }

  private pivotToDevices(data: DeviceData): void {
    Application.lib.Navigation.openAppPageInNewTab(
      `/risk-management-resources`,
      {
        filters: [
          {
            [DevicesColumnKeys.DEVICE_NAME]: {
              complexEnumFilter: [{
                label: data.deviceName,
                value: data.deviceId,
                type: data.deviceType
              }]
            }
          },
          {
            [DevicesColumnKeys.COMPANY]: { exactMatch: this.component.companyId },
          }
        ]
      }
    );
  }

  private pivotToVulnerabilities(data: VulnerabilityData): void {
    Application.lib.Navigation.openAppPageInNewTab(
      `/risk-management-vulnerabilities`,
      {
        filters: [
          {
            [VulnerabilitiesColumnKeys.NAME]: {
              complexEnumFilter: [{
                label: data.vulnerabilityName,
                value: data.vulnerabilityName
              }]
            }
          },
          {
            [VulnerabilitiesColumnKeys.COMPANY]: { exactMatch: this.component.companyId },
          }
        ]
      }
    );
  }

  private clickOnGetLicense(): void {
    NavigationHelper.drillDownToMyCompany(NavigationConstants.MY_COMPANY.TABS.LICENSING);
  }

  private openLink(link: string): void {
    if (link) {
      window.open(link, '_blank');
    }
  }

  private disableLabelSwitch(ev: CustomEvent): void {
    this.view.disableSwitch(true);
  }

  private async handleLabelSwitchChange(ev: CustomEvent): Promise<void> {
    this.component.type = ev.detail.selectedValue;
    this.view.dom.$inputSearch.value = this.lastSearchValues[this.component.type];
    this.searchKey = this.lastSearchValues[this.component.type];
    this.view.rerenderView({
      data: this.applyFilter(),
      risksType: ev.detail.selectedValue,
    });
  }

  private async handleSearchInputChange(ev: CustomEvent): Promise<void> {
    if (ev.type === 'change') {
      this.searchKey = ev.detail.sourceEvent;
    }

    if (this.searchKey.replace(/\s+/g, '') === '' || this.searchKey.length < 2) {
      this.searchKey = '';
    }

    this.lastSearchValues[this.component.type] = this.searchKey;
    this.view.rerenderView({
      data: this.applyFilter(),
      risksType: this.component.type,
    });
  }

  private applyFilter(): RisksData {
    const filteredData = structuredClone(this.data),
      filteredRisksCategories = {};
    const risksCategories: EntityData = this.data[this.component.type];
    if (!risksCategories.hasOwnProperty('errorCode')) {
      for (const entity in risksCategories) {
        if (risksCategories.hasOwnProperty(entity)) {
          const risks = risksCategories[entity]['items'];

          const filteredRisks = risks.filter(risk => {
            return this.areAllWordsFromSearchTextIncludedInString(risk['name'], this.searchKey);
          });

          filteredRisksCategories[entity] = {
            items: filteredRisks,
            total: filteredRisks.length,
          };
        }
      }

      filteredData[this.component.type] = filteredRisksCategories;
    }

    return filteredData;
  }

  private areAllWordsFromSearchTextIncludedInString(textToSearchIn: string, searchText: string): boolean {
    if (searchText === '') {
      return true;
    }

    searchText = searchText.toLowerCase();
    const filterWords = searchText.split(/\s+/);

    for (const filterWord of filterWords) {
      if (!textToSearchIn.trim().toLowerCase().includes(filterWord)) {
        return false;
      }
    }

    return true;
  }
}
